const REQUIRED_ENV_KEYS = [
  'VITE_API_URL',
  'VITE_APP_ENV',
  'VITE_OAUTH2_AUTHORIZATION_URL',
  'VITE_OAUTH2_AUTHORIZATION_TENANT_URL',
  'VITE_OAUTH2_CLIENT_ID',
  'VITE_OAUTH2_REDIRECT_URI',
  'VITE_REVERB_APP_KEY',
  'VITE_REVERB_HOST',
  'VITE_REVERB_WSS_PORT',
  'VITE_REVERB_PORT',
  'VITE_REVERB_SCHEME',
  'VITE_MIXPANEL_TOKEN',
] as const;

const APP_ENVIRONMENTS = ['development', 'staging', 'production'] as const;

type EnvValues = Record<Env, string>;
type TestEnvGlobal = typeof globalThis & {
  __VITE_ENV__?: Partial<EnvValues>;
};

export type Env = (typeof REQUIRED_ENV_KEYS)[number];
export type AppEnvironment = (typeof APP_ENVIRONMENTS)[number];

const getRawEnv = (): Partial<EnvValues> => {
  const testEnv = (globalThis as TestEnvGlobal).__VITE_ENV__ ?? {};
  return {
    ...testEnv,
    ...(import.meta.env as Partial<EnvValues>),
  };
};

const validateRequiredEnv = (rawEnv: Partial<EnvValues>): EnvValues => {
  const missingKeys = REQUIRED_ENV_KEYS.filter((key) => {
    const value = rawEnv[key];
    return typeof value !== 'string' || value.trim().length === 0;
  });

  if (missingKeys.length > 0) {
    throw new Error(
      `Missing required Vite environment variables: ${missingKeys.join(
        ', ',
      )}. Ensure all variables are defined with the VITE_ prefix.`,
    );
  }

  return rawEnv as EnvValues;
};

let cachedEnv: EnvValues | null = null;

const getValidatedEnv = (): EnvValues => {
  if (!cachedEnv) {
    cachedEnv = validateRequiredEnv(getRawEnv());
  }

  return cachedEnv;
};

const validateAppEnvironment = (value: string): AppEnvironment => {
  if ((APP_ENVIRONMENTS as readonly string[]).includes(value)) {
    return value as AppEnvironment;
  }

  throw new Error(
    `Invalid VITE_APP_ENV value "${value}". Expected one of: ${APP_ENVIRONMENTS.join(
      ', ',
    )}.`,
  );
};

export const env = (key: Env): string => getValidatedEnv()[key];

export const appEnvironment = (): AppEnvironment => {
  return validateAppEnvironment(env('VITE_APP_ENV'));
};

export const isDevelopment = (): boolean => appEnvironment() === 'development';

export const isStaging = (): boolean => appEnvironment() === 'staging';

export const isProduction = (): boolean => appEnvironment() === 'production';
